import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { createVaccination } from 'apiServices/reducer/dialogApiService';
import '../../../styles/user.css';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import "flatpickr/dist/themes/material_green.css";
import { Calendar } from 'lucide-react';
import CustomSuccessModal from '../../../components/CustomSuccessModal';

/** Parse API / grid date strings as a local calendar day (avoids UTC YYYY-MM-DD off-by-one). */
function parseVaccinationDate(value) {
    if (!value) return new Date();
    if (value instanceof Date) {
        const d = new Date(value.getTime());
        d.setHours(0, 0, 0, 0);
        return d;
    }
    const s = String(value).trim();
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (iso) {
        const y = Number(iso[1]);
        const m = Number(iso[2]) - 1;
        const day = Number(iso[3]);
        return new Date(y, m, day);
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? new Date() : d;
}

/** YYYY-MM-DD in local time (avoid toISOString shifting the day). */
function formatDateOnlyLocal(d) {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

const emptyVaccinationForm = {
    vaccine_name: '',
    dose_number: '',
    manufacturer: '',
    date: null,
};

const VaccinationModal = ({ show, handleClose, rowData, refreshVaccinationData, userId }) => {
    const datePickerRef = useRef(null);
    const { control, handleSubmit, reset, clearErrors } = useForm({
        defaultValues: emptyVaccinationForm,
    });
    const [fieldErrors, setFieldErrors] = useState({ vaccine_name: false, dose_number: false, manufacturer: false, date: false });
    const inputErrorStyle = { border: '1px solid #dc3545', boxShadow: 'none' };
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const dispatch = useDispatch();
    const token = useSelector((state) => state.apiService?.token);

    const clearForm = () => {
        reset(emptyVaccinationForm);
        datePickerRef.current?.flatpickr?.clear();
        setFieldErrors({ vaccine_name: false, dose_number: false, manufacturer: false, date: false });
        clearErrors();
    };

    useEffect(() => {
        if (!show) return;
        if (rowData?.vaccination_id) {
            reset({
                vaccine_name: rowData.vaccine_name || '',
                dose_number: rowData.dose_number || '',
                manufacturer: rowData.manufacturer || '',
                date: rowData.date ? parseVaccinationDate(rowData.date) : null,
            });
        } else {
            reset(emptyVaccinationForm);
        }
        setFieldErrors({ vaccine_name: false, dose_number: false, manufacturer: false, date: false });
        clearErrors();
    }, [show, rowData, reset, clearErrors]);

    // Stable options — new object each render makes react-flatpickr destroy/recreate the instance.
    const flatpickrOptions = useMemo(
        () => ({
            dateFormat: 'm/d/Y',
            maxDate: 'today',
            allowInput: true,
            clickOpens: true,
            theme: 'material_green',
        }),
        []
    );

    const handleModalHide = () => {
        clearForm();
        handleClose();
    };

    const onSave = async (data) => {
        const errorsObj = {
            vaccine_name: !data.vaccine_name?.trim(),
            dose_number: !data.dose_number?.toString().trim(),
            manufacturer: !data.manufacturer?.trim(),
            date: !(data.date instanceof Date) || Number.isNaN(data.date.getTime())
        };
        setFieldErrors(errorsObj);
        if (Object.values(errorsObj).some(Boolean)) return;
        const formBody = {
            VaccinationID: rowData?.vaccination_id || null,
            VaccineName: data.vaccine_name,
            DoseNumber: data.dose_number?.toString().trim() || '',
            Manufacturer: data.manufacturer,
            Date: formatDateOnlyLocal(data.date),
            UserID: userId,
            token,
        };

        try {
            console.log(formBody, 'formBody for createVaccination');
            const response = await dispatch(createVaccination(formBody)).unwrap();
            console.log(response, 'createVaccination response');
            setSuccessMessage('Vaccination record saved successfully!');
            setShowSuccessModal(true);
            refreshVaccinationData();
            clearForm();
            handleClose();
        } catch (error) {
            console.error('API Error:', error);

        }
    };

    return (
        <>
            <Modal show={show} onHide={handleModalHide} centered dialogClassName="wider-modal" backdrop="static" keyboard={false} enforceFocus={false}>
                <Modal.Header closeButton className='modal-header-bg'>
                    <Modal.Title className='text-capitalize'>
                        {rowData?.vaccination_id ? 'Edit Vaccination' : 'Add Vaccination'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit(onSave)} autoComplete="off">
                        <Form.Group controlId="VaccineName" className="mb-3">
                            <Form.Label className='mb-1 fw-bold'>Vaccine Name</Form.Label>
                            <Controller
                                name="vaccine_name"
                                control={control}
                                render={({ field }) => (
                                    <Form.Control
                                        {...field}
                                        type="text"
                                        placeholder="Enter vaccine name"
                                        className={`form-control custom-textarea ${fieldErrors.vaccine_name ? 'error-border' : ''}`}
                                        style={fieldErrors.vaccine_name ? inputErrorStyle : {}}
                                        onChange={e => {
                                            field.onChange(e);
                                            setFieldErrors(prev => ({ ...prev, vaccine_name: false }));
                                        }}
                                    />
                                )}
                            />
                        </Form.Group>

                        <Form.Group controlId="DoseNumber" className="mb-3">
                            <Form.Label className='mb-1 fw-bold'>Dose</Form.Label>
                            <Controller
                                name="dose_number"
                                control={control}
                                render={({ field }) => (
                                    <Form.Control
                                        {...field}
                                        type="text"
                                        placeholder="Enter dose"
                                        className={`form-control custom-textarea ${fieldErrors.dose_number ? 'error-border' : ''}`}
                                        style={fieldErrors.dose_number ? inputErrorStyle : {}}
                                        onChange={e => {
                                            field.onChange(e.target.value);
                                            setFieldErrors(prev => ({ ...prev, dose_number: false }));
                                        }}
                                    />
                                )}
                            />
                        </Form.Group>

                        <Form.Group controlId="Manufacturer" className="mb-3">
                            <Form.Label className='mb-1 fw-bold'>Manufacturer Name</Form.Label>
                            <Controller
                                name="manufacturer"
                                control={control}
                                render={({ field }) => (
                                    <Form.Control
                                        {...field}
                                        type="text"
                                        placeholder="Enter manufacturer name"
                                        className={`form-control custom-textarea ${fieldErrors.manufacturer ? 'error-border' : ''}`}
                                        style={fieldErrors.manufacturer ? inputErrorStyle : {}}
                                        onChange={e => {
                                            field.onChange(e);
                                            setFieldErrors(prev => ({ ...prev, manufacturer: false }));
                                        }}
                                    />
                                )}
                            />
                        </Form.Group>

                        <Form.Group controlId="Date" className="mb-3">
                            <Form.Label className='mb-1 fw-bold'>Date</Form.Label>
                            <Controller
                                name="date"
                                control={control}
                                render={({ field }) => (
                                    <div style={{ position: "relative" }}>
                                        <Flatpickr
                                            ref={datePickerRef}
                                            value={field.value}
                                            onChange={(selectedDates) => {
                                                const picked = selectedDates?.[0];
                                                if (picked) {
                                                    const d = new Date(picked);
                                                    d.setHours(0, 0, 0, 0);
                                                    field.onChange(d);
                                                } else {
                                                    field.onChange(null);
                                                }
                                                setFieldErrors(prev => ({ ...prev, date: false }));
                                            }}
                                            options={flatpickrOptions}
                                            className={`form-control custom-textarea ${fieldErrors.date ? 'error-border' : ''}`}
                                            placeholder="Select date"
                                            style={fieldErrors.date ? inputErrorStyle : {}}
                                        />
                                        <span
                                            style={{
                                                position: "absolute",
                                                right: 12,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                cursor: "pointer",
                                                color: "var(--pmr-text-muted)",
                                                height: "36px",
                                                display: "flex",
                                                alignItems: "center"
                                            }}
                                            onClick={() => datePickerRef.current?.flatpickr?.open()}
                                        >
                                            <Calendar size={20} />
                                        </span>
                                    </div>
                                )}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="modal-header-bg justify-content-center">
                    <Button variant="primary" className="me-2" onClick={handleSubmit(onSave)}>
                        Save
                    </Button>
                    <Button variant="danger" className="ms-2" onClick={handleModalHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <CustomSuccessModal
                show={showSuccessModal}
                onHide={() => { setShowSuccessModal(false); setSuccessMessage(''); }}
                message={successMessage}
            />
            <style>{`
                .error-border { border: 1px solid #dc3545 !important; box-shadow: none !important; }
                
                /* Simple fix for scroll positioning */
                .flatpickr-calendar {
                    z-index: 999999 !important;
                    position: fixed !important;
                }
            `}</style>
        </>
    );
};

export default VaccinationModal;